import "@/App.css";
import "@/design-refinement.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Toaster } from "@/components/ui/sonner";
import { SettingsProvider, useSettings } from "@/contexts/SettingsContext";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import SchedulePage from "@/pages/SchedulePage";
import TravelPage from "@/pages/TravelPage";
import RegistryPage, { RegistryGiftsPage } from "@/pages/RegistryPage";
import FAQsPage from "@/pages/FAQsPage";
import RSVPPage from "@/pages/RSVPPage";
import RsvpClosedPage from "@/pages/RsvpClosedPage";
import AdminPage from '@/pages/AdminPage';

const SITE_URL = 'https://lexiandjames.com';
const PAGE_METADATA = {
  '/': {
    title: 'Lexi Till & James Graessle | Wedding',
    description: 'The wedding website of Lexi Till and James Graessle',
  },
  '/schedule': {
    title: 'Schedule | Lexi & James',
    description: 'The schedule for Lexi and James’s wedding weekend in Baltimore.',
  },
  '/travel': {
    title: 'Travel & Hotels | Lexi & James',
    description: 'Travel information and recommended hotels for Lexi and James’s Baltimore wedding.',
  },
  '/registry': {
    title: 'Registry | Lexi & James',
    description: 'Wedding gifts and Galápagos honeymoon experiences for Lexi and James.',
  },
  '/registry/gifts': {
    title: 'All Gifts | Lexi & James',
    description: 'View the complete wedding registry for Lexi and James.',
  },
  '/faqs': {
    title: 'FAQs | Lexi & James',
    description: 'Answers to common questions about Lexi and James’s wedding weekend.',
  },
  '/rsvp': {
    title: 'RSVP | Lexi & James',
    description: 'Online RSVP information for Lexi and James’s wedding.',
  },
  '/admin': {
    title: 'Admin | Lexi & James',
    description: 'Private wedding website administration.',
    noIndex: true,
  },
};

function RouteMetadata({ showRsvp }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = PAGE_METADATA[pathname] || PAGE_METADATA['/'];
    const canonicalUrl = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;
    const upsertMeta = (selector, attributes, content) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    document.title = page.title;
    upsertMeta('meta[name="description"]', { name: 'description' }, page.description);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, page.title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, page.description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, page.title);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, page.description);

    const canonical = document.head.querySelector('link[rel="canonical"]');
    canonical?.setAttribute('href', canonicalUrl);

    const shouldNoIndex = page.noIndex || (pathname === '/rsvp' && !showRsvp);
    const existingRobots = document.head.querySelector('meta[name="robots"]');
    if (shouldNoIndex) {
      upsertMeta('meta[name="robots"]', { name: 'robots' }, 'noindex, nofollow');
    } else {
      existingRobots?.remove();
    }
  }, [pathname, showRsvp]);

  return null;
}

// Elegant loading placeholder shown while the backend warms up (serverless cold start)
const PageLoader = () => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center">
    <div className="text-center">
      <p className="text-xs uppercase tracking-[0.25em] text-olive mb-4 animate-pulse">
        Just a moment
      </p>
      <h2 className="font-giaza text-4xl text-forest mb-3">Loading</h2>
      <div className="flex justify-center gap-1.5 mt-4">
        <span className="w-1.5 h-1.5 rounded-full bg-forest/40 animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-forest/40 animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-1.5 h-1.5 rounded-full bg-forest/40 animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

// Inner component that uses settings context
function AppRoutes() {
  const { showRsvp, showRegistry, loading } = useSettings();

  return (
    <>
      <RouteMetadata showRsvp={showRsvp} />
      <Navigation />
      <main>
        <Routes>
          {/* Always visible routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/travel" element={<TravelPage />} />
          <Route path="/faqs" element={<FAQsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          
          {/* During cold start, show graceful loader for gated routes */}
          {loading ? (
            <>
              <Route path="/rsvp" element={<PageLoader />} />
              <Route path="/registry" element={<PageLoader />} />
              <Route path="/registry/gifts" element={<PageLoader />} />
            </>
          ) : (
            <>
              {/* Registry routes - conditionally rendered */}
              {showRegistry && (
                <>
                  <Route path="/registry" element={<RegistryPage />} />
                  <Route path="/registry/gifts" element={<RegistryGiftsPage />} />
                </>
              )}
              
              <Route
                path="/rsvp"
                element={showRsvp ? <RSVPPage /> : <RsvpClosedPage />}
              />
            </>
          )}
        </Routes>
      </main>
      <Footer />
      <Toaster position="bottom-center" />
    </>
  );
}

function App() {
  return (
    <div className="site-shell min-h-screen bg-linen">
      <BrowserRouter>
        <SettingsProvider>
          <AppRoutes />
        </SettingsProvider>
        <Analytics />
        <SpeedInsights />
      </BrowserRouter>
    </div>
  );
}

export default App;
